import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { SHIPPING_METHOD, type ShippingMethod } from '@frontend/api';
import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Tooltip,
  OutlineCircleInfoIcon,
  Collapse,
  VStack,
  Input,
  Select,
  Box,
  Text,
  Flex,
} from '@frontend/components';

import {
  PAYMENT_METHOD,
  SHIPPING_CARRIER,
  SHIPPING_GROUPING,
  SHIPPING_TYPE,
} from 'constants/all';

import { useAppSelector } from 'store/hooks';

import {
  SHIPPING_CARRIER_LABEL,
  SHIPPING_METHOD_LABEL,
  SHIPPING_METHOD_RATES,
} from '../constants';
import { setShippingChoice, setShippingRate } from '../data/actions';
import {
  getPaymentMethod,
  getShippingAccount,
  getShippingInformation,
  getShippingRate,
  getShippingType,
} from '../data/reducer';
import {
  ShippingAccount,
  ShippingCarrier,
  ShippingRate,
  ShippingType,
} from '../data/types';

const { EXPEDITED, ...shippingMethods } = SHIPPING_METHOD;

const SHIPPING_TYPE_LABEL = {
  [SHIPPING_TYPE.PICKUP]: 'No shipping fees',
  [SHIPPING_TYPE.CUSTOMER_ACCOUNT]: 'Use customer shipping account',
  [SHIPPING_TYPE.SUPPLIER_ACCOUNT]: 'Bill customer at time of shipment',
};

const getCarrierOption = (carrier: ShippingCarrier) => {
  return {
    value: carrier,
    label: SHIPPING_CARRIER_LABEL[carrier],
  };
};

const getMethodOption = (method: ShippingMethod) => {
  return {
    value: method,
    label: SHIPPING_METHOD_LABEL[method],
  };
};

const DEFAULT_SHIPPING_ACCOUNT_VALUES = {
  customersAccountNumber: undefined,
  customersCarrier: SHIPPING_CARRIER.UPS,
  shippingMethod: SHIPPING_METHOD.EARLY_OVERNIGHT,
  shipWhen: SHIPPING_GROUPING.BULK,
};

const useShippingOptionDisabledMessages = () => {
  const paymentMethod = useAppSelector(getPaymentMethod);
  const shippingInformation = useAppSelector(getShippingInformation);
  const hasShippingInformation = Boolean(shippingInformation);
  return {
    [SHIPPING_TYPE.SUPPLIER_ACCOUNT]:
      paymentMethod !== PAYMENT_METHOD.PURCHASE_ORDER
        ? `Payment Method must be Purchase Order ${
            !hasShippingInformation
              ? 'and a Ship To Address must be provided '
              : ''
          }to select this option`
        : !hasShippingInformation
        ? 'A Ship To Address must be provided above to select this option'
        : null,
    [SHIPPING_TYPE.CUSTOMER_ACCOUNT]:
      paymentMethod === PAYMENT_METHOD.NONE
        ? `Payment Method must be Purchase Order or Credit Card ${
            !hasShippingInformation
              ? 'and a Ship To Address must be provided '
              : ''
          }to select this option`
        : !hasShippingInformation
        ? 'A Ship To Address must be provided above to select this option'
        : null,
    [SHIPPING_TYPE.PICKUP]: hasShippingInformation
      ? null
      : 'A Ship To Address must be provided above to select this option',
  };
};

export const ShippingOptions = () => {
  const dispatch = useDispatch();
  const shippingType = useAppSelector(getShippingType);
  const shippingAccount = useAppSelector(getShippingAccount);
  const shippingRate = useAppSelector(getShippingRate);
  const optionDisabledMessages = useShippingOptionDisabledMessages();

  const shippingAccountFormValues = useMemo(
    () => shippingAccount ?? DEFAULT_SHIPPING_ACCOUNT_VALUES,
    [shippingAccount],
  );

  const updateShippingAccount = useCallback(
    (values: Partial<ShippingAccount>) => {
      const nextShippingAccount = {
        ...shippingAccountFormValues,
        ...values,
      };
      dispatch(
        setShippingChoice({
          shippingAccount: nextShippingAccount,
          shippingType: SHIPPING_TYPE.CUSTOMER_ACCOUNT,
          shippingRate: undefined,
        }),
      );
    },
    [shippingAccountFormValues, dispatch],
  );

  const shippingRateFormValue = useMemo(
    () => shippingRate ?? SHIPPING_METHOD_RATES[0],
    [shippingRate],
  );

  const updateShippingRate = useCallback(
    (shippingRate?: ShippingRate) => {
      dispatch(setShippingRate({ shippingRate }));
    },
    [dispatch],
  );

  const handleChangeShippingType = useCallback(
    (shippingType: ShippingType | 'none') => {
      const nextShippingType =
        shippingType === 'none' ? undefined : shippingType;
      const nextShippingAccount =
        shippingType === SHIPPING_TYPE.CUSTOMER_ACCOUNT
          ? shippingAccountFormValues
          : undefined;
      const nextShippingRate =
        shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT
          ? shippingRateFormValue
          : undefined;
      dispatch(
        setShippingChoice({
          shippingType: nextShippingType,
          shippingAccount: nextShippingAccount,
          shippingRate: nextShippingRate,
        }),
      );
    },
    [dispatch, shippingAccountFormValues, shippingRateFormValue],
  );

  return (
    <AccordionItem>
      {({ isExpanded }) => (
        <>
          <AccordionButton>
            <Flex gap={1}>
              <Box flexShrink={0}>Shipping Options</Box>
              {!isExpanded ? (
                <Text
                  color="feature.secondary"
                  noOfLines={1}
                  whiteSpace="nowrap"
                >
                  {shippingType ? SHIPPING_TYPE_LABEL[shippingType] : 'None'}
                </Text>
              ) : null}
            </Flex>
          </AccordionButton>
          <AccordionPanel>
            <FormControl>
              <FormLabel>Shipping Fees</FormLabel>
              <RadioGroup
                value={shippingType ?? 'none'}
                onChange={handleChangeShippingType}
              >
                <Radio value="none">None</Radio>
                <Tooltip
                  label={optionDisabledMessages[SHIPPING_TYPE.SUPPLIER_ACCOUNT]}
                  placement="right"
                  shouldWrapChildren={true}
                >
                  <Radio
                    value={SHIPPING_TYPE.SUPPLIER_ACCOUNT}
                    isDisabled={
                      !!optionDisabledMessages[SHIPPING_TYPE.SUPPLIER_ACCOUNT]
                    }
                  >
                    {SHIPPING_TYPE_LABEL[SHIPPING_TYPE.SUPPLIER_ACCOUNT]}
                  </Radio>
                </Tooltip>
                <Collapse in={shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT}>
                  <VStack pl={3} width="100%">
                    <FormControl>
                      <FormLabel>Shipping Method</FormLabel>
                      <RadioGroup
                        value={shippingRateFormValue.method}
                        onChange={(value) => {
                          const shippingRate = SHIPPING_METHOD_RATES.find(
                            (rate) => rate.method === value,
                          );
                          updateShippingRate(shippingRate);
                        }}
                      >
                        {SHIPPING_METHOD_RATES.map((rate) => (
                          <Radio key={rate.method} value={rate.method}>
                            {SHIPPING_METHOD_LABEL[rate.method]}
                          </Radio>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </VStack>
                </Collapse>
                <Tooltip
                  label={optionDisabledMessages[SHIPPING_TYPE.CUSTOMER_ACCOUNT]}
                  placement="right"
                  shouldWrapChildren={true}
                >
                  <Radio
                    value={SHIPPING_TYPE.CUSTOMER_ACCOUNT}
                    isDisabled={
                      !!optionDisabledMessages[SHIPPING_TYPE.CUSTOMER_ACCOUNT]
                    }
                  >
                    {SHIPPING_TYPE_LABEL[SHIPPING_TYPE.CUSTOMER_ACCOUNT]}
                  </Radio>
                </Tooltip>
                <Collapse in={shippingType === SHIPPING_TYPE.CUSTOMER_ACCOUNT}>
                  <VStack p={0.5} pl={3} width="100%">
                    <FormControl isRequired>
                      <FormLabel>Shipping Account #</FormLabel>
                      <Input
                        key={shippingAccountFormValues.customersAccountNumber}
                        defaultValue={
                          shippingAccountFormValues.customersAccountNumber
                        }
                        onBlur={(event) =>
                          updateShippingAccount({
                            customersAccountNumber: event.target.value,
                          })
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Carrier</FormLabel>
                      <Select
                        isClearable={false}
                        value={getCarrierOption(
                          shippingAccountFormValues.customersCarrier,
                        )}
                        options={Object.values(SHIPPING_CARRIER).map(
                          getCarrierOption,
                        )}
                        onChange={({ value }) =>
                          updateShippingAccount({ customersCarrier: value })
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Method</FormLabel>
                      <Select
                        isClearable={false}
                        value={getMethodOption(
                          shippingAccountFormValues.shippingMethod,
                        )}
                        options={Object.values(shippingMethods).map(
                          getMethodOption,
                        )}
                        onChange={({ value }) =>
                          updateShippingAccount({ shippingMethod: value })
                        }
                      />
                    </FormControl>
                  </VStack>
                </Collapse>
                <Tooltip
                  label={optionDisabledMessages[SHIPPING_TYPE.PICKUP]}
                  placement="right"
                  shouldWrapChildren={true}
                >
                  <Radio
                    value={SHIPPING_TYPE.PICKUP}
                    isDisabled={!!optionDisabledMessages[SHIPPING_TYPE.PICKUP]}
                  >
                    {SHIPPING_TYPE_LABEL[SHIPPING_TYPE.PICKUP]}
                    {!optionDisabledMessages[SHIPPING_TYPE.PICKUP] ? (
                      <Tooltip label="This option is for customer pickup, local delivery, or other arrangements that preclude shipping fees.">
                        <OutlineCircleInfoIcon
                          color="feature.secondary"
                          boxSize={3}
                          verticalAlign="bottom"
                        />
                      </Tooltip>
                    ) : null}
                  </Radio>
                </Tooltip>
              </RadioGroup>
            </FormControl>
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
};
